import { Checkbox as CheckboxPrimitive } from '@base-ui/react'
import { Check, Minus } from 'lucide-react'
import { useId } from 'react'

import { cn } from '@/lib/cn'

/* Checkbox.
 *
 * Built on Base UI's checkbox primitive: `CheckboxPrimitive.Root` renders a
 * `role="checkbox"` span with a hidden native `<input type="checkbox">` beside
 * it, so form submission, the label click target and the screen-reader
 * announcement all still come from a real input, while the visible box is
 * styled directly off the `data-checked` / `data-indeterminate` / `data-disabled`
 * attributes the primitive writes. That replaces the `peer-*` sibling
 * construction this component used, and with it the imperative effect that had
 * to set `input.indeterminate` by hand — on the primitive it is a plain prop.
 *
 * The mark is a CHILD of the box here rather than a sibling: `data-*` selectors
 * are set on the root itself, so unlike `peer-checked` they reach into the
 * subtree and no longer need the three-element flat stack.
 *
 * `onChange` is not available — `CheckboxRootProps` omits it — so the callback
 * is `onCheckedChange(checked)`, which is the one intentional API change in
 * this component.
 */

export interface CheckboxProps
  extends Omit<CheckboxPrimitive.Root.Props, 'className' | 'render' | 'children'> {
  label: string
  /** Sits under the label in muted text — the consequence of ticking it. */
  hint?: string
  className?: string
}

export function Checkbox({
  label,
  hint,
  className,
  disabled,
  indeterminate,
  id,
  ...props
}: CheckboxProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const labelId = `${inputId}-label`
  const hintId = hint ? `${inputId}-hint` : undefined

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <CheckboxPrimitive.Root
        id={inputId}
        disabled={disabled}
        indeterminate={indeterminate}
        aria-labelledby={labelId}
        aria-describedby={hintId}
        className={cn(
          'relative mt-px flex size-4 shrink-0 cursor-pointer items-center justify-center',
          'rounded-[4px] border',
          'transition-[background-color,border-color,box-shadow]',
          'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
          'border-border-default bg-surface',
          'hover:border-neutral-400',
          'data-checked:bg-brand data-checked:border-brand',
          'data-indeterminate:bg-brand data-indeterminate:border-brand',
          'focus-visible:shadow-[var(--focus-ring)] focus-visible:outline-none',
          'data-disabled:bg-surface-disabled data-disabled:border-border-subtle',
          'data-disabled:cursor-not-allowed',
        )}
        {...props}
      >
        {/* aria-hidden on the indicator, not just on the icons: without it the
            indicator's own subtree joins the accessible name and the box is
            announced as "✓ Notify by email". */}
        <CheckboxPrimitive.Indicator
          render={
            <span
              aria-hidden
              className="text-text-on-brand pointer-events-none flex items-center justify-center"
            />
          }
        >
          {indeterminate ? (
            <Minus size={11} aria-hidden />
          ) : (
            <Check size={11} aria-hidden />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>

      <span className="flex min-w-0 flex-col gap-0.5">
        {/* `htmlFor` points at the hidden native input the primitive renders,
            which is what keeps a click on the words toggling the box. The
            accessible name comes from `aria-labelledby` on the root, because
            `htmlFor` names the input and not the `role="checkbox"` span. */}
        <label
          id={labelId}
          htmlFor={inputId}
          className={cn(
            'text-[13px] leading-tight font-normal',
            disabled
              ? 'text-text-disabled cursor-not-allowed'
              : 'text-text-body cursor-pointer',
          )}
        >
          {label}
        </label>
        {hint ? (
          <span id={hintId} className="text-text-muted text-[12px] leading-snug font-light">
            {hint}
          </span>
        ) : null}
      </span>
    </div>
  )
}
